import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { equals, isDefined } from '@softline/core';
import {
  MetaControl,
  MetaControlNameDirective,
  MetaForm,
  MetaFormDirective,
  MetaFormHelper,
} from '@softline/dynamic';
import { LogPipe } from '@softline/ui-core';
import { MetaQueryBarItemComponent } from './meta-query-bar-item/meta-query-bar-item.component';

@Component({
  selector: 'soft-meta-query-bar',
  imports: [
    CommonModule,
    MetaControlNameDirective,
    MetaQueryBarItemComponent,
    ReactiveFormsModule,
    MetaFormDirective,
    LogPipe,
  ],
  templateUrl: './meta-query-bar.component.html',
  styleUrl: './meta-query-bar.component.scss',
})
//extends WithMetaForm()
export class MetaQueryBarComponent<T extends object> {
  _formInitialized = signal(false);
  form = new FormGroup({});
  private formValue = toSignal(this.form.valueChanges);

  metaForm = input.required<MetaForm<T>>({ alias: 'metaForm' });

  metaFormEffect = effect(
    () => {
      const metaForm = this.metaForm();
      MetaFormHelper.updateMeta(this.form, metaForm as any);
      this._formInitialized.set(true);
    },
    { allowSignalWrites: true }
  );

  query = input.required<T | null>();
  queryEffect = effect(
    () => {
      const query = this.query();
      const formInitialized = this._formInitialized();
      if (!formInitialized || !query) return;
      this.form.patchValue(query, { emitEvent: false });
    },
    { allowSignalWrites: true }
  );

  queryChange = output<T | null>();

  items = computed(() => {
    const meta = this.metaForm();
    const query = this.query();
    const formInitialized = this._formInitialized();

    const returnValue: { key: string; meta: MetaControl<any> }[] = [];
    if (!formInitialized || !query || !meta) return returnValue;

    for (const [key, value] of Object.entries(query)) {
      const control = meta[key];
      if (isDefined(value) && isDefined(control))
        returnValue.push({ key, meta: control });
    }
    return returnValue;
  });
  constructor(destroyRef: DestroyRef) {
    this.form.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe((value) => {
      if (!this._formInitialized() || equals(value, this.query())) return;
      this.queryChange.emit(value as any);
    });
  }
}
